import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { App as Application } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TAB_PATHS } from '../components/layout/tabs';

/**
 * Le bouton retour d'Android, qui reculait hors de l'application.
 *
 * Sur Android, le retour est le geste le plus employe : il vit dans le
 * pouce, pas dans l'ecran. Personne ne l'ecoutait, et Capacitor s'en
 * tenait alors a son comportement par defaut — fermer l'activite. Depuis
 * l'ecran d'inscription, un seul appui renvoyait a l'ecran d'accueil du
 * telephone. Mesure sur un Galaxy A07.
 *
 * Depuis un ecran interieur on recule donc d'un cran ; depuis une racine
 * — un onglet, la connexion, le choix du spot — on rend la main au
 * systeme, comme le fait n'importe quelle application Android.
 *
 * Un ecran ouvert par une notification n'a rien derriere lui : reculer
 * ne menerait nulle part, on rejoint alors l'accueil.
 *
 * iOS n'a pas de bouton retour, et sa coquille n'embarque pas ce greffon :
 * la garde de plateforme evite d'appeler un natif absent.
 */
const RACINES = new Set<string>([...TAB_PATHS, '/login', '/choose-spot', '/personalize']);

export function useBoutonRetour(connecte: boolean): void {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;

    const attente = Application.addListener('backButton', () => {
      if (RACINES.has(pathname)) {
        void Application.minimizeApp();
        return;
      }
      if (window.history.length > 1) navigate(-1);
      else navigate(connecte ? '/app/feed' : '/login', { replace: true });
    });

    return () => {
      void attente.then((ecoute) => ecoute.remove()).catch(() => undefined);
    };
  }, [pathname, navigate, connecte]);
}
